import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { api } from '../api/api'
import { useAuth } from './AuthContext'

type Year = number
export type Division = 'lmo3' | 'spb'

interface YearContextType {
  year: Year
  setYear: (year: Year) => void
  availableYears: Year[]
  division: Division
  setDivision: (division: Division) => void
}

const YearContext = createContext<YearContextType | undefined>(undefined)

const calendarYear = Math.max(2025, new Date().getFullYear())
const fallbackYears = Array.from(
  { length: calendarYear - 2025 + 1 },
  (_, index) => calendarYear - index
)

function readSavedYear(): Year | null {
  const saved = localStorage.getItem('selected_year')
  return /^20\d{2}$/.test(saved || '') ? Number(saved) : null
}

export function YearProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const initialSavedYear = useRef(readSavedYear())
  const hasExplicitYear = useRef(initialSavedYear.current !== null)
  const [year, setYearState] = useState<Year>(initialSavedYear.current || calendarYear)
  const [availableYears, setAvailableYears] = useState<Year[]>(fallbackYears)
  const [division, setDivision] = useState<Division>(() =>
    localStorage.getItem('selected_division') === 'spb' ? 'spb' : 'lmo3'
  )

  useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false
    api.getAvailableYears()
      .then(({ years }) => {
        if (cancelled) return
        const normalizedYears = Array.from(
          new Set(years.filter((value) => Number.isInteger(value) && value >= 2000 && value <= 2100))
        ).sort((a, b) => b - a)
        if (normalizedYears.length === 0) return

        setAvailableYears(normalizedYears)
        setYearState((currentYear) =>
          !hasExplicitYear.current || !normalizedYears.includes(currentYear)
            ? normalizedYears[0]
            : currentYear
        )
      })
      .catch(() => {
        // Calendar-based fallback remains usable while the API is unavailable.
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  useEffect(() => {
    localStorage.setItem('selected_year', year.toString())
  }, [year])

  useEffect(() => {
    localStorage.setItem('selected_division', division)
  }, [division])

  const setYear = (newYear: Year) => {
    hasExplicitYear.current = true
    setYearState(newYear)
  }

  return (
    <YearContext.Provider value={{ year, setYear, availableYears, division, setDivision }}>
      {children}
    </YearContext.Provider>
  )
}

export function useYear() {
  const context = useContext(YearContext)
  if (context === undefined) {
    throw new Error('useYear must be used within a YearProvider')
  }
  return context
}
